import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Package, Search } from 'lucide-react';

import { ProduitAdminListItem } from 'api/product';
import { GenericColors } from 'api/tenant';
import { DisplayTypeEnum } from 'api/files';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { GenericLoading } from 'components/ui/Loader';
import { Button } from 'components/ui/Button';
import GenericBadge from 'components/ui/Badge';
import { SwitchComponent } from 'components/forms/Switch';
import { NsKey } from 'enums/NsKey';
import { productsAPI } from 'services/swagger-api';

import {
  PlateformeProduitTunnelDto,
  getPlateformeCatalogue,
  getPlateformeProduitTunnels,
  linkPlateformeProduit,
  linkPlateformeTunnel,
  unlinkPlateformeProduit,
  unlinkPlateformeTunnel
} from '../api/catalogueApi';

interface PlateformeCatalogueManageProps {
  plateformeId: string;
}

const showError = (message: string) =>
  toasterService.showToast({
    message,
    displayType: DisplayTypeEnum.Toaster,
    color: GenericColors.Red
  });

const showSuccess = (message: string) =>
  toasterService.showToast({
    message,
    displayType: DisplayTypeEnum.Toaster,
    color: GenericColors.Green
  });

/**
 * "Gestion" view of the Catalogue tab — master/detail: every produit on the
 * left (Attribué/Disponible badge, explicit Attribuer/Retirer button), the
 * selected produit's tunnels on the right with a switch per tunnel. Each
 * toggle saves immediately; the back enforces the subset rule and the removal
 * block, the UI only makes them visible.
 */
export const PlateformeCatalogueManage: React.FC<
  PlateformeCatalogueManageProps
> = ({ plateformeId }) => {
  const { t } = useTranslation(NsKey.Common);

  const [produits, setProduits] = useState<ProduitAdminListItem[]>([]);
  const [attributed, setAttributed] = useState<Set<string>>(new Set());
  const [tunnelsCount, setTunnelsCount] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [tunnels, setTunnels] = useState<PlateformeProduitTunnelDto[] | null>(
    null
  );
  const [tunnelsLoading, setTunnelsLoading] = useState(false);
  /** Id (produit ou tunnel) dont l'action est en cours — évite le double clic. */
  const [pendingId, setPendingId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      productsAPI.getAdminProduits({
        getGenericListCommandDto: { pageNumber: 1, pageSize: 100 }
      }),
      getPlateformeCatalogue(plateformeId)
    ])
      .then(([produitsRes, catalogue]) => {
        if (cancelled) return;
        setProduits(produitsRes.items ?? []);
        setAttributed(new Set(catalogue.produits.map(p => p.produitId)));
        setTunnelsCount(
          Object.fromEntries(
            catalogue.produits.map(p => [p.produitId, p.tunnels.length])
          )
        );
      })
      .catch(() => {
        if (!cancelled) showError(t('PLATEFORME_CATALOGUE.LOAD_ERROR'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [plateformeId, t]);

  const selectProduit = useCallback(
    async (produitId: string) => {
      setSelectedId(produitId);
      setTunnels(null);
      setTunnelsLoading(true);
      try {
        const res = await getPlateformeProduitTunnels(plateformeId, produitId);
        setTunnels(res.tunnels);
      } catch {
        showError(t('PLATEFORME_CATALOGUE.LOAD_ERROR'));
      } finally {
        setTunnelsLoading(false);
      }
    },
    [plateformeId, t]
  );

  const toggleProduit = useCallback(
    async (produit: ProduitAdminListItem) => {
      setPendingId(produit.id);
      try {
        if (attributed.has(produit.id)) {
          const res = await unlinkPlateformeProduit(plateformeId, produit.id);
          if (!res.isSuccess) {
            showError(res.errorMessage ?? t('PLATEFORME_CATALOGUE.LOAD_ERROR'));
            return;
          }
          setAttributed(prev => {
            const next = new Set(prev);
            next.delete(produit.id);
            return next;
          });
          showSuccess(t('PLATEFORME_CATALOGUE.PRODUCT_REMOVED'));
        } else {
          const res = await linkPlateformeProduit(plateformeId, produit.id);
          if (!res.isSuccess) {
            showError(res.errorMessage ?? t('PLATEFORME_CATALOGUE.LOAD_ERROR'));
            return;
          }
          setAttributed(prev => new Set(prev).add(produit.id));
          showSuccess(t('PLATEFORME_CATALOGUE.PRODUCT_ATTRIBUTED'));
          // Flux guidé : le produit vient d'être attribué → on ouvre ses tunnels.
          selectProduit(produit.id);
        }
      } finally {
        setPendingId(null);
      }
    },
    [attributed, plateformeId, selectProduit, t]
  );

  const toggleTunnel = useCallback(
    async (tunnel: PlateformeProduitTunnelDto) => {
      if (!selectedId) return;
      setPendingId(tunnel.tunnelId);
      try {
        const res = tunnel.isLinked
          ? await unlinkPlateformeTunnel(plateformeId, tunnel.tunnelId)
          : await linkPlateformeTunnel(plateformeId, tunnel.tunnelId);
        if (!res.isSuccess) {
          showError(res.errorMessage ?? t('PLATEFORME_CATALOGUE.LOAD_ERROR'));
          return;
        }
        setTunnels(prev =>
          (prev ?? []).map(item =>
            item.tunnelId === tunnel.tunnelId
              ? { ...item, isLinked: !tunnel.isLinked }
              : item
          )
        );
        setTunnelsCount(prev => ({
          ...prev,
          [selectedId]: (prev[selectedId] ?? 0) + (tunnel.isLinked ? -1 : 1)
        }));
        showSuccess(
          tunnel.isLinked
            ? t('PLATEFORME_CATALOGUE.TUNNEL_REMOVED')
            : t('PLATEFORME_CATALOGUE.TUNNEL_ATTRIBUTED')
        );
      } finally {
        setPendingId(null);
      }
    },
    [plateformeId, selectedId, t]
  );

  const visibleProduits = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const sorted = [...produits].sort((a, b) => a.label.localeCompare(b.label));
    if (!needle) return sorted;
    return sorted.filter(
      p =>
        p.label.toLowerCase().includes(needle) ||
        p.code.toLowerCase().includes(needle)
    );
  }, [produits, search]);

  const selected = produits.find(p => p.id === selectedId) ?? null;
  const selectedAttributed = selectedId != null && attributed.has(selectedId);
  const linkedTunnels = (tunnels ?? []).filter(tu => tu.isLinked).length;

  if (loading) {
    return <GenericLoading loading />;
  }

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-2">
      {/* Colonne produits */}
      <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t('PLATEFORME_CATALOGUE.SEARCH_PRODUCT')}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm focus:border-primary focus:bg-white focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
          <span className="flex-shrink-0 font-mono text-xs tracking-wide text-gray-500">
            {t('PLATEFORME_CATALOGUE.ATTRIBUTED_COUNT', {
              count: attributed.size,
              total: produits.length
            })}
          </span>
        </div>

        {visibleProduits.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">
            {t('PLATEFORME_CATALOGUE.NO_PRODUCTS')}
          </p>
        ) : (
          <div className="flex max-h-[62vh] flex-col gap-1.5 overflow-y-auto pr-1">
            {visibleProduits.map(produit => {
              const isAttributed = attributed.has(produit.id);
              const count = tunnelsCount[produit.id] ?? 0;
              const removeBlocked = isAttributed && count > 0;
              return (
                <div
                  key={produit.id}
                  // Toute la ligne sélectionne le produit (les boutons d'action
                  // stoppent la propagation) — cliquer pile sur le titre n'est
                  // plus nécessaire.
                  onClick={() => selectProduit(produit.id)}
                  className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 transition-all ${
                    selectedId === produit.id
                      ? 'border-primary bg-primary/5 ring-1 ring-primary'
                      : 'border-transparent hover:bg-gray-50'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => selectProduit(produit.id)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left focus:outline-none"
                  >
                    <span
                      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${
                        isAttributed
                          ? 'bg-primary/10 text-primary'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      <Package size={16} />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-gray-900">
                        {produit.label}
                      </span>
                      <span className="block truncate font-mono text-xs text-gray-400">
                        {produit.code}
                        {isAttributed && (
                          <>
                            {' · '}
                            {t('PLATEFORME_CATALOGUE.TUNNELS_COUNT', {
                              count
                            })}
                          </>
                        )}
                      </span>
                    </span>
                  </button>
                  {/* Colonnes fixes : badge et action gardent la même largeur
                      d'une ligne à l'autre, quel que soit leur libellé. */}
                  <span className="flex w-28 flex-shrink-0 justify-center">
                    <GenericBadge
                      badgeType="sm-bold"
                      color={
                        isAttributed ? GenericColors.Primary : GenericColors.Gray
                      }
                      label={
                        isAttributed
                          ? t('PLATEFORME_CATALOGUE.ATTRIBUTED')
                          : t('PLATEFORME_CATALOGUE.AVAILABLE')
                      }
                    />
                  </span>
                  <span
                    className="w-28 flex-shrink-0"
                    title={
                      removeBlocked
                        ? t('PLATEFORME_CATALOGUE.REMOVE_BLOCKED')
                        : undefined
                    }
                  >
                    {isAttributed ? (
                      <Button
                        color="light"
                        className="w-full justify-center"
                        disabled={pendingId === produit.id || removeBlocked}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          toggleProduit(produit);
                        }}
                      >
                        {t('PLATEFORME_CATALOGUE.REMOVE')}
                      </Button>
                    ) : (
                      <Button
                        color="primary"
                        className="w-full justify-center"
                        disabled={pendingId === produit.id}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          toggleProduit(produit);
                        }}
                      >
                        {t('PLATEFORME_CATALOGUE.ATTRIBUTE')}
                      </Button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Colonne tunnels du produit sélectionné */}
      <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
        {!selected ? (
          <p className="py-8 text-center text-sm text-gray-500">
            {t('PLATEFORME_CATALOGUE.SELECT_PRODUCT')}
          </p>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-xs font-medium uppercase tracking-widest text-primary">
                  {t('PLATEFORME_CATALOGUE.TUNNELS_OVERLINE')}
                </p>
                <p className="truncate text-xl font-semibold text-gray-900">
                  {selected.label}
                </p>
                <p className="truncate font-mono text-xs text-gray-400">
                  {selected.code}
                </p>
              </div>
              {selectedAttributed && tunnels != null && (
                <GenericBadge
                  badgeType="sm-bold"
                  color={GenericColors.Primary}
                  label={t('PLATEFORME_CATALOGUE.ACTIVE_COUNT', {
                    count: linkedTunnels,
                    total: tunnels.length
                  })}
                />
              )}
            </div>

            {!selectedAttributed ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <p className="text-sm text-gray-500">
                  {t('PLATEFORME_CATALOGUE.PRODUCT_NOT_ATTRIBUTED')}
                  <br />
                  {t('PLATEFORME_CATALOGUE.ATTRIBUTE_FIRST')}
                </p>
                <Button
                  color="primary"
                  disabled={pendingId === selected.id}
                  onClick={() => toggleProduit(selected)}
                >
                  {t('PLATEFORME_CATALOGUE.ATTRIBUTE')}
                </Button>
              </div>
            ) : tunnelsLoading || tunnels == null ? (
              <GenericLoading loading />
            ) : tunnels.length === 0 ? (
              <p className="py-8 text-center text-sm text-gray-500">
                {t('PLATEFORME_CATALOGUE.NO_TUNNELS')}
              </p>
            ) : (
              <div className="flex max-h-[56vh] flex-col gap-2 overflow-y-auto pr-1">
                {tunnels.map(tunnel => (
                  <div
                    key={tunnel.tunnelId}
                    className={`flex items-center gap-3 rounded-xl border p-3 transition-all ${
                      tunnel.isLinked
                        ? 'border-primary/20 bg-primary/5'
                        : 'border-gray-200 bg-white'
                    }`}
                  >
                    <span
                      className={`h-2 w-2 flex-shrink-0 rounded-full ${
                        tunnel.isActive ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                      title={
                        tunnel.isActive
                          ? undefined
                          : t('PLATEFORME_CATALOGUE.INACTIVE')
                      }
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block truncate text-sm font-semibold ${
                          tunnel.isLinked ? 'text-gray-900' : 'text-gray-500'
                        }`}
                      >
                        {tunnel.label}
                      </span>
                      <span className="block truncate font-mono text-xs text-gray-400">
                        {tunnel.code}
                      </span>
                    </span>
                    <SwitchComponent
                      name={`tunnel-${tunnel.tunnelId}`}
                      checked={tunnel.isLinked}
                      disabled={pendingId === tunnel.tunnelId}
                      onChange={() => toggleTunnel(tunnel)}
                    />
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
